import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, Upload, Package, ShoppingBag, MessageSquare, Settings as SettingsIcon, Save, Ticket, Star, FolderTree, LayoutDashboard, Boxes, Wallet, Users, FileText, Receipt, Barcode, Printer } from "lucide-react";
import JsBarcode from "jsbarcode";
import { api, imageUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DashboardTab, InventoryTab, ExpensesTab, CustomersTab, SalesTab, printInvoice } from "./ErpTabs";

const field = "w-full border border-border bg-background px-3 py-2 font-body text-sm focus:outline-none focus:border-foreground";
const empty = { name: "", category: "men", price: "", original_price: "", cost_price: "", description: "", fabric: "Pure Cotton", sizes: "", colors: "", images: [], featured: false, in_stock: true, stock: {} };

function ProductModal({ open, onClose, onSaved, editing, categories }) {
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (editing) setForm({ ...editing, price: editing.price, original_price: editing.original_price || "", cost_price: editing.cost_price ?? "", sizes: (editing.sizes || []).join(", "), colors: (editing.colors || []).join(", "), stock: editing.stock || {} });
    else setForm(empty);
  }, [editing, open]);

  if (!open) return null;

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setForm((s) => ({ ...s, images: [...s.images, ...paths] }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name || !form.price) { toast.error("Name and price required"); return; }
    const sizeList = form.sizes ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : [];
    const stockClean = {};
    sizeList.forEach((s) => { stockClean[s] = parseInt(form.stock?.[s]) || 0; });
    const payload = {
      name: form.name, category: form.category, price: parseFloat(form.price),
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      cost_price: parseFloat(form.cost_price) || 0,
      description: form.description, fabric: form.fabric,
      sizes: sizeList,
      colors: form.colors ? form.colors.split(",").map((s) => s.trim()).filter(Boolean) : [],
      images: form.images, featured: !!form.featured, in_stock: !!form.in_stock, stock: stockClean,
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      onSaved(); onClose();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background w-full max-w-2xl max-h-[90vh] overflow-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-3xl">{editing ? "Edit product" : "New product"}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><label className="overline">Name</label><input className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pf-name" /></div>
          <div><label className="overline">Category</label>
            <select className={field} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pf-category">
              {(categories && categories.length ? categories : [{ slug: "men", name: "Men" }, { slug: "children", name: "Children" }]).map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <div><label className="overline">Fabric</label><input className={field} value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} /></div>
          <div><label className="overline">Price ₹ (sale rate)</label><input className={field} type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pf-price" /></div>
          <div><label className="overline">Original price ₹</label><input className={field} type="number" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} /></div>
          <div className="col-span-2"><label className="overline">Purchase rate ₹ (aapka cost — profit calculation ke liye, customers ko nahi dikhta)</label><input className={field} type="number" min="0" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} data-testid="pf-cost-price" placeholder="700" /></div>
          <div className="col-span-2"><label className="overline">Description</label><textarea className={`${field} h-24 resize-none`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div><label className="overline">Sizes (comma separated)</label><input className={field} value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="S, M, L" /></div>
          <div><label className="overline">Colors (comma separated)</label><input className={field} value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="Indigo" /></div>
          {(() => {
            const sizeList = form.sizes ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : [];
            if (!sizeList.length) return null;
            return (
              <div className="col-span-2">
                <label className="overline">Stock per size (pieces) — 0 = out of stock</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid="stock-editor">
                  {sizeList.map((s) => (
                    <div key={s} className="flex items-center gap-2 border border-border px-2 py-1.5">
                      <span className="font-mono-label text-xs w-12 shrink-0">{s}</span>
                      <input type="number" min="0" className="w-full bg-background font-body text-sm focus:outline-none" value={form.stock?.[s] ?? ""} onChange={(e) => setForm({ ...form, stock: { ...form.stock, [s]: parseInt(e.target.value) || 0 } })} data-testid={`stock-${s}`} placeholder="3" />
                    </div>
                  ))}
                </div>
                <p className="font-body text-xs text-muted-foreground mt-1">Order aane par ye apne aap kam hoga; 0 hote hi wo size "Out of stock" dikhega.</p>
              </div>
            );
          })()}
          <div className="col-span-2">
            <label className="overline">Images</label>
            <div className="flex gap-2 flex-wrap mt-2">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-20 h-24 bg-muted">
                  <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setForm({ ...form, images: form.images.filter((_, x) => x !== i) })} className="absolute -top-2 -right-2 bg-[hsl(var(--primary))] text-white rounded-full w-5 h-5 flex items-center justify-center"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-20 h-24 border border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground" data-testid="pf-upload">
                <Upload className="w-4 h-4" /><span className="text-[10px]">{uploading ? "…" : "Add"}</span>
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} />
            </div>
            <p className="overline text-muted-foreground mt-2">Or paste an image URL below</p>
            <div className="flex gap-2 mt-1">
              <input className={field} placeholder="https://…" id="url-input" />
              <button onClick={() => { const el = document.getElementById("url-input"); if (el.value) { setForm((s) => ({ ...s, images: [...s.images, el.value] })); el.value = ""; } }} className="px-4 border border-border text-sm">Add</button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pf-featured" /> Featured</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} /> In stock</label>
        </div>
        <button onClick={save} data-testid="pf-save" className="w-full mt-6 bg-[hsl(var(--primary))] text-white py-3 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors">Save product</button>
      </div>
    </div>
  );
}

function BarcodeSvg({ code }) {
  const ref = useRef();
  useEffect(() => {
    if (ref.current) {
      try { JsBarcode(ref.current, code, { format: "CODE128", width: 1.1, height: 32, fontSize: 10, margin: 2, displayValue: true }); } catch {}
    }
  }, [code]);
  return <svg ref={ref} />;
}

function BarcodeLabelsModal({ product, onClose }) {
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({});
  const printRef = useRef();

  useEffect(() => {
    if (!product) return;
    api.get(`/admin/products/${product.id}/labels`).then((r) => {
      setData(r.data);
      const c = {};
      r.data.labels.forEach((l) => { c[l.size || "-"] = 1; });
      setCounts(c);
    }).catch(() => toast.error("Labels load nahi hue"));
  }, [product]);

  if (!product) return null;

  const labels = [];
  if (data) {
    data.labels.forEach((l) => {
      const n = Math.min(50, Math.max(0, parseInt(counts[l.size || "-"]) || 0));
      for (let i = 0; i < n; i++) labels.push(l);
    });
  }

  const print = () => {
    const w = window.open("", "_blank");
    if (!w || !printRef.current) return;
    w.document.write(`<!doctype html><html><head><title>Barcode Labels - ${data.name}</title><meta charset="utf-8"><style>
      body{font-family:Arial,sans-serif;margin:8px}
      .lbl{display:inline-block;vertical-align:top;width:46mm;border:1px dashed #999;padding:3mm 2mm;margin:2mm;text-align:center;page-break-inside:avoid}
      .brand{font-size:8px;letter-spacing:2px;color:#555}
      .nm{font-size:10px;font-weight:bold;margin:2px 0;line-height:1.2}
      .meta{font-size:9px;color:#333}
      .pr{font-size:11px;font-weight:bold;margin-top:2px}
      .mrp{font-size:9px;color:#888;text-decoration:line-through;margin-right:4px}
      .off{font-size:8px;color:#9C3D32;font-weight:bold}
      svg{max-width:100%}
      @media print{.noprint{display:none}}
    </style></head><body>${printRef.current.innerHTML}<div class="noprint" style="text-align:center;margin-top:10px"><button onclick="window.print()" style="padding:8px 20px;cursor:pointer">Print labels</button></div></body></html>`);
    w.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background w-full max-w-3xl max-h-[90vh] overflow-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="barcode-modal">
        <div className="flex justify-between items-center mb-2">
          <h2 className="font-display text-3xl">Barcode labels — {product.name}</h2>
          <button onClick={onClose} data-testid="barcode-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="font-body text-xs text-muted-foreground mb-6">Kapdon par lagane wale price tags. Har size ka apna barcode hai — counter par scan karte hi sale mein item aur size apne aap aa jayega. Base code: <span className="font-mono-label">{data?.barcode}</span></p>
        {!data ? <p className="font-body text-muted-foreground">Loading…</p> : (
          <>
            <div className="flex flex-wrap items-end gap-3 mb-6 border border-border p-4 bg-card">
              {data.labels.map((l) => (
                <div key={l.size || "-"}>
                  <label className="overline text-muted-foreground">Size {l.size || "—"} · kitne labels</label>
                  <input type="number" min="0" max="50" className="w-24 border border-border bg-background px-3 py-2 font-body text-sm mt-1 focus:outline-none focus:border-foreground" value={counts[l.size || "-"] ?? 1} onChange={(e) => setCounts({ ...counts, [l.size || "-"]: e.target.value })} data-testid={`label-count-${l.size || "na"}`} />
                </div>
              ))}
              <button onClick={print} disabled={!labels.length} data-testid="print-labels-btn" className="ml-auto inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-6 py-2.5 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors disabled:opacity-50"><Printer className="w-4 h-4" /> Print {labels.length} labels</button>
            </div>
            <div ref={printRef} data-testid="labels-preview">
              {labels.map((l, i) => (
                <div key={`${l.code}-${i}`} className="lbl" style={{ display: "inline-block", verticalAlign: "top", width: 170, border: "1px dashed #999", padding: 8, margin: 6, textAlign: "center" }}>
                  <div className="brand" style={{ fontSize: 8, letterSpacing: 2, color: "#555" }}>NAVRANG UDAIPUR</div>
                  <div className="nm" style={{ fontSize: 10, fontWeight: "bold", margin: "2px 0", lineHeight: 1.2 }}>{data.name}</div>
                  <div className="meta" style={{ fontSize: 9, color: "#333" }}>Size: {l.size || "—"}</div>
                  <div className="pr" style={{ fontSize: 11, fontWeight: "bold", marginTop: 2 }}>
                    {data.original_price > data.price && <span className="mrp" style={{ fontSize: 9, color: "#888", textDecoration: "line-through", marginRight: 4 }}>₹{data.original_price}</span>}
                    ₹{data.price}
                    {data.original_price > data.price && <span className="off" style={{ fontSize: 8, color: "#9C3D32", fontWeight: "bold", marginLeft: 4 }}>{Math.round((1 - data.price / data.original_price) * 100)}% OFF</span>}
                  </div>
                  <BarcodeSvg code={l.code} />
                </div>
              ))}
              {labels.length === 0 && <p className="font-body text-sm text-muted-foreground">Label count 0 hai — upar count badhayein.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState({ email: "", address: "" });
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: "", discount_percent: "" });
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: "", image: "" });
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ name: "", city: "", rating: 5, text: "" });
  const catFileRef = useRef();

  useEffect(() => { if (admin === false) navigate("/admin/login"); }, [admin, navigate]);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  const loadCoupons = () => api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  const loadCategories = () => api.get("/categories/all").then((r) => setCategories(r.data)).catch(() => {});
  const loadReviews = () => api.get("/reviews/all").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => {
    loadProducts();
    loadCoupons();
    loadCategories();
    loadReviews();
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings((s) => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  const uploadCatImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setNewCategory((s) => ({ ...s, image: data.path })); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
  };
  const createCategory = async () => {
    if (!newCategory.name.trim()) { toast.error("Category name required"); return; }
    try { await api.post("/categories", { name: newCategory.name.trim(), image: newCategory.image, active: true }); toast.success("Category created"); setNewCategory({ name: "", image: "" }); loadCategories(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const toggleCategory = async (c) => { try { await api.patch(`/categories/${c.id}`, { name: c.name, image: c.image || "", active: !c.active }); loadCategories(); } catch { toast.error("Failed"); } };
  const deleteCategory = async (id) => { if (!window.confirm("Delete this category?")) return; await api.delete(`/categories/${id}`); loadCategories(); };

  const createReview = async () => {
    if (!newReview.name.trim() || !newReview.text.trim()) { toast.error("Name and review text required"); return; }
    try { await api.post("/reviews", { ...newReview, rating: parseInt(newReview.rating) || 5 }); toast.success("Review added"); setNewReview({ name: "", city: "", rating: 5, text: "" }); loadReviews(); }
    catch { toast.error("Failed"); }
  };
  const toggleReview = async (r) => { try { await api.patch(`/reviews/${r.id}`, { name: r.name, city: r.city, rating: r.rating, text: r.text, active: !r.active }); loadReviews(); } catch { toast.error("Failed"); } };
  const deleteReview = async (id) => { if (!window.confirm("Delete this review?")) return; await api.delete(`/reviews/${id}`); loadReviews(); };

  const createCoupon = async () => {
    if (!newCoupon.code.trim() || !newCoupon.discount_percent) { toast.error("Code and discount % required"); return; }
    try {
      await api.post("/coupons", { code: newCoupon.code.trim(), discount_percent: parseFloat(newCoupon.discount_percent), active: true });
      toast.success("Coupon created"); setNewCoupon({ code: "", discount_percent: "" }); loadCoupons();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const toggleCoupon = async (c) => {
    try { await api.patch(`/coupons/${c.id}`, { code: c.code, discount_percent: c.discount_percent, active: !c.active }); loadCoupons(); }
    catch { toast.error("Failed"); }
  };
  const deleteCoupon = async (id) => { if (!window.confirm("Delete this coupon?")) return; await api.delete(`/coupons/${id}`); loadCoupons(); };

  const saveSettings = async () => {
    try { await api.put("/settings", settings); toast.success("Store details updated"); }
    catch { toast.error("Save failed"); }
  };

  const [pwd, setPwd] = useState({ current: "", next: "" });
  const changePassword = async () => {
    if (!pwd.current || !pwd.next) { toast.error("Dono password fields bharein"); return; }
    if (pwd.next.length < 8) { toast.error("Naya password kam se kam 8 characters ka ho"); return; }
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password successfully badal gaya");
      setPwd({ current: "", next: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Password change failed"); }
  };

  const heroFileRef = useRef();
  const uploadHero = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...s, hero_image: data.path }));
      toast.success("Image uploaded — ab Save details dabayein");
    } catch { toast.error("Upload failed"); }
  };

  const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/products/${id}`); toast.success("Deleted"); loadProducts(); };

  const updateOrder = async (id, patch) => {
    try {
      const { data } = await api.patch(`/orders/${id}`, patch);
      setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
      toast.success("Order updated");
    } catch { toast.error("Update failed"); }
  };

  if (!admin) return <div className="min-h-screen flex items-center justify-center font-body text-muted-foreground">Loading…</div>;

  const tabs = [["dashboard", "Dashboard", LayoutDashboard], ["products", "Products", Package], ["categories", "Categories", FolderTree], ["inventory", "Inventory", Boxes], ["orders", "Orders", ShoppingBag], ["sales", "Sales", Receipt], ["customers", "Customers", Users], ["expenses", "Expenses", Wallet], ["coupons", "Coupons", Ticket], ["reviews", "Reviews", Star], ["messages", "Messages", MessageSquare], ["settings", "Settings", SettingsIcon]];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <span className="font-display text-2xl">Navrang <span className="overline text-[hsl(var(--primary))]">Admin</span></span>
            <nav className="hidden md:flex gap-1">
              {tabs.map(([k, label, Icon]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-body ${tab === k ? "bg-[hsl(var(--secondary))] text-white" : "text-foreground/70"}`}>
                  <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
                </button>
              ))}
            </nav>
          </div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {tab === "dashboard" && <DashboardTab />}
        {tab === "inventory" && <InventoryTab />}
        {tab === "expenses" && <ExpensesTab />}
        {tab === "customers" && <CustomersTab />}
        {tab === "sales" && <SalesTab />}

        {tab === "products" && (
          <div data-testid="admin-products">
            <div className="flex justify-between items-center mb-8">
              <h1 className="font-display text-4xl">Products ({products.length})</h1>
              <button onClick={() => { setEditing(null); setModal(true); }} data-testid="add-product-btn"
                className="flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-5 py-3 text-sm font-body hover:bg-[hsl(var(--secondary))] transition-colors">
                <Plus className="w-4 h-4" /> Add product
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
              {products.map((p) => (
                <div key={p.id} className="bg-background p-4 flex gap-4" data-testid={`admin-product-${p.id}`}>
                  <img src={imageUrl(p.images?.[0])} alt={p.name} className="w-20 h-24 object-cover bg-muted" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg leading-tight truncate">{p.name}</p>
                    <p className="overline text-muted-foreground capitalize">{p.category} · ₹{p.price}</p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => { setEditing(p); setModal(true); }} className="p-2 border border-border" data-testid={`edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(p.id)} className="p-2 border border-border text-[hsl(var(--primary))]" data-testid={`delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="admin-orders">
            <h1 className="font-display text-4xl mb-8">Orders ({orders.length})</h1>
            {orders.length === 0 ? <p className="font-body text-muted-foreground">No orders yet.</p> : (
              <div className="border border-border divide-y divide-border">
                {orders.map((o) => (
                  <div key={o.id} className="p-5 grid md:grid-cols-12 gap-4 text-sm font-body items-center" data-testid={`admin-order-${o.id}`}>
                    <div className="md:col-span-3">
                      <p className="font-mono-label text-[hsl(var(--primary))] text-xs">{o.tracking_id}</p>
                      <p className="font-600">{o.customer_name}</p>
                      <p className="text-muted-foreground text-xs">{o.phone} · {o.city}</p>
                      <button onClick={() => printInvoice(o, settings)} data-testid={`invoice-${o.id}`} className="mt-2 inline-flex items-center gap-1.5 border border-border px-3 py-1 text-xs font-body hover:border-foreground transition-colors"><FileText className="w-3.5 h-3.5" /> Invoice</button>
                    </div>
                    <div className="md:col-span-3 text-muted-foreground text-xs">{o.items?.map((i) => `${i.name} ×${i.qty}`).join(", ")}</div>
                    <div className="md:col-span-2">
                      <p className="font-600">₹{o.total}</p>
                      <span className="overline text-[hsl(var(--primary))] capitalize">{o.payment_method} · {o.status}</span>
                      {o.upi_ref && <p className="text-xs text-muted-foreground">UTR: {o.upi_ref}</p>}
                    </div>
                    <div className="md:col-span-2">
                      <label className="overline text-muted-foreground">Stage</label>
                      <select className="w-full border border-border bg-background px-2 py-1.5 text-xs mt-1" value={o.fulfillment || "processing"}
                        onChange={(e) => updateOrder(o.id, { fulfillment: e.target.value })} data-testid={`fulfillment-${o.id}`}>
                        <option value="processing">Confirmed</option>
                        <option value="packed">Packed</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="overline text-muted-foreground">Payment</label>
                      <select className="w-full border border-border bg-background px-2 py-1.5 text-xs mt-1" value={o.status || "pending"}
                        onChange={(e) => updateOrder(o.id, { status: e.target.value })} data-testid={`status-${o.id}`}>
                        <option value="pending">Pending</option>
                        <option value="payment_submitted">Payment submitted</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="paid">Paid</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "categories" && (
          <div data-testid="admin-categories" className="max-w-3xl">
            <h1 className="font-display text-4xl mb-8">Categories ({categories.length})</h1>
            <div className="border border-border p-6 bg-card mb-8">
              <p className="overline text-muted-foreground mb-4">Create new category</p>
              <div className="flex flex-col sm:flex-row gap-3 items-start">
                <input className="flex-1 border border-border bg-background px-3 py-2.5 font-body text-sm focus:outline-none focus:border-foreground" placeholder="Category name e.g. Women, Kurta, Festive" value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} data-testid="new-category-name" />
                <button type="button" onClick={() => catFileRef.current?.click()} className="inline-flex items-center gap-2 border border-border px-4 py-2.5 font-body text-sm hover:border-foreground transition-colors" data-testid="category-image-btn">
                  <Upload className="w-4 h-4" strokeWidth={1.5} /> {newCategory.image ? "Image ✓" : "Image"}
                </button>
                <input ref={catFileRef} type="file" accept="image/*" className="hidden" onChange={uploadCatImage} />
                <button onClick={createCategory} data-testid="create-category-btn" className="bg-[hsl(var(--primary))] text-white px-6 py-2.5 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors">Create</button>
              </div>
              <p className="font-body text-xs text-muted-foreground mt-2">Naya category banate hi wo Home page cards, Shop filter aur menu me apne aap aa jayega.</p>
            </div>
            {categories.length === 0 ? <p className="font-body text-muted-foreground">No categories yet.</p> : (
              <div className="grid sm:grid-cols-2 gap-px bg-border border border-border">
                {categories.map((c) => (
                  <div key={c.id} className="bg-background p-4 flex gap-4 items-center" data-testid={`admin-category-${c.slug}`}>
                    {c.image ? <img src={imageUrl(c.image)} alt={c.name} className="w-16 h-16 object-cover bg-muted" /> : <div className="w-16 h-16 bg-muted flex items-center justify-center"><FolderTree className="w-5 h-5 text-muted-foreground" /></div>}
                    <div className="flex-1">
                      <p className="font-display text-xl">{c.name}</p>
                      <p className="overline text-muted-foreground">/{c.slug} · {c.active ? "Active" : "Hidden"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleCategory(c)} data-testid={`toggle-category-${c.slug}`} className={`px-3 py-1.5 text-xs font-body border ${c.active ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "border-border text-muted-foreground"}`}>{c.active ? "On" : "Off"}</button>
                      <button onClick={() => deleteCategory(c.id)} className="p-2 border border-border text-[hsl(var(--primary))]" data-testid={`delete-category-${c.slug}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "reviews" && (
          <div data-testid="admin-reviews" className="max-w-3xl">
            <h1 className="font-display text-4xl mb-8">Customer reviews ({reviews.length})</h1>
            <div className="border border-border p-6 bg-card mb-8 space-y-3">
              <p className="overline text-muted-foreground">Add a real customer review</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <input className="border border-border bg-background px-3 py-2.5 font-body text-sm focus:outline-none focus:border-foreground" placeholder="Customer name" value={newReview.name} onChange={(e) => setNewReview({ ...newReview, name: e.target.value })} data-testid="new-review-name" />
                <input className="border border-border bg-background px-3 py-2.5 font-body text-sm focus:outline-none focus:border-foreground" placeholder="City" value={newReview.city} onChange={(e) => setNewReview({ ...newReview, city: e.target.value })} data-testid="new-review-city" />
                <select className="border border-border bg-background px-3 py-2.5 font-body text-sm focus:outline-none focus:border-foreground" value={newReview.rating} onChange={(e) => setNewReview({ ...newReview, rating: e.target.value })} data-testid="new-review-rating">
                  {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{`${n} Star${n > 1 ? "s" : ""}`}</option>)}
                </select>
              </div>
              <textarea className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm h-24 resize-none focus:outline-none focus:border-foreground" placeholder="Review text…" value={newReview.text} onChange={(e) => setNewReview({ ...newReview, text: e.target.value })} data-testid="new-review-text" />
              <button onClick={createReview} data-testid="create-review-btn" className="bg-[hsl(var(--primary))] text-white px-6 py-2.5 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors">Add review</button>
            </div>
            {reviews.length === 0 ? <p className="font-body text-muted-foreground">No reviews yet.</p> : (
              <div className="border border-border divide-y divide-border">
                {reviews.map((r) => (
                  <div key={r.id} className="p-5 flex justify-between gap-4" data-testid={`admin-review-${r.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-display text-xl">{r.name}</p>
                        {r.city && <span className="overline text-muted-foreground">· {r.city}</span>}
                        <span className="flex gap-0.5">{[...Array(Math.max(1, Math.min(5, r.rating || 5)))].map((_, s) => <Star key={s} className="w-3 h-3 fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" />)}</span>
                      </div>
                      <p className="font-body text-sm text-muted-foreground mt-2">{r.text}</p>
                    </div>
                    <div className="flex items-start gap-2 shrink-0">
                      <button onClick={() => toggleReview(r)} data-testid={`toggle-review-${r.id}`} className={`px-3 py-1.5 text-xs font-body border ${r.active ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "border-border text-muted-foreground"}`}>{r.active ? "Shown" : "Hidden"}</button>
                      <button onClick={() => deleteReview(r.id)} className="p-2 border border-border text-[hsl(var(--primary))]" data-testid={`delete-review-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "coupons" && (
          <div data-testid="admin-coupons" className="max-w-2xl">
            <h1 className="font-display text-4xl mb-8">Coupon codes ({coupons.length})</h1>
            <div className="border border-border p-6 bg-card mb-8">
              <p className="overline text-muted-foreground mb-4">Create new coupon</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input className="flex-1 border border-border bg-background px-3 py-2.5 font-body text-sm uppercase focus:outline-none focus:border-foreground" placeholder="CODE e.g. DIWALI20" value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })} data-testid="new-coupon-code" />
                <input type="number" min="1" max="90" className="sm:w-40 border border-border bg-background px-3 py-2.5 font-body text-sm focus:outline-none focus:border-foreground" placeholder="Discount %" value={newCoupon.discount_percent} onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: e.target.value })} data-testid="new-coupon-discount" />
                <button onClick={createCoupon} data-testid="create-coupon-btn" className="bg-[hsl(var(--primary))] text-white px-6 py-2.5 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors">Create</button>
              </div>
            </div>
            {coupons.length === 0 ? <p className="font-body text-muted-foreground">No coupons yet.</p> : (
              <div className="border border-border divide-y divide-border">
                {coupons.map((c) => (
                  <div key={c.id} className="p-4 flex items-center justify-between" data-testid={`coupon-${c.id}`}>
                    <div>
                      <p className="font-mono-label text-lg tracking-widest">{c.code}</p>
                      <p className="overline text-muted-foreground">{c.discount_percent}% off · {c.active ? "Active" : "Inactive"}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => toggleCoupon(c)} data-testid={`toggle-coupon-${c.id}`}
                        className={`px-4 py-2 text-xs font-body border ${c.active ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "border-border text-muted-foreground"}`}>
                        {c.active ? "On" : "Off"}
                      </button>
                      <button onClick={() => deleteCoupon(c.id)} className="p-2 border border-border text-[hsl(var(--primary))]" data-testid={`delete-coupon-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "messages" && (
          <div data-testid="admin-messages">
            <h1 className="font-display text-4xl mb-8">Messages ({contacts.length})</h1>
            {contacts.length === 0 ? <p className="font-body text-muted-foreground">No messages yet.</p> : (
              <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
                {contacts.map((c) => (
                  <div key={c.id} className="bg-background p-5">
                    <p className="font-600 font-body">{c.name} <span className="text-muted-foreground font-400">· {c.email}</span></p>
                    <p className="font-body text-sm text-muted-foreground mt-2">{c.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "settings" && (
          <div data-testid="admin-settings" className="max-w-xl">
            <h1 className="font-display text-4xl mb-8">Store settings</h1>
            <div className="space-y-5 border border-border p-8 bg-card">
              <div>
                <label className="overline text-muted-foreground">Store email</label>
                <input className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.email} onChange={(e) => setSettings({ ...settings, email: e.target.value })} data-testid="settings-email" placeholder="hello@navrangudaipur.com" />
              </div>
              <div>
                <label className="overline text-muted-foreground">Store address</label>
                <textarea className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 h-28 resize-none focus:outline-none focus:border-foreground" value={settings.address} onChange={(e) => setSettings({ ...settings, address: e.target.value })} data-testid="settings-address" placeholder="Shop no, Street, Udaipur, Rajasthan 313001" />
              </div>
              <p className="font-body text-xs text-muted-foreground">Ye email aur address Contact page par customers ko dikhega.</p>

              <div className="border-t border-border pt-6 mt-2">
                <label className="overline text-muted-foreground">Order notification email</label>
                <input className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.notify_email || ""} onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })} data-testid="settings-notify-email" placeholder="jise order aane par email chahiye" />
                <p className="font-body text-xs text-muted-foreground mt-1">Har naye order par is email par notification jayega.</p>
              </div>

              <div className="border-t border-border pt-6 mt-2">
                <p className="font-display text-2xl">UPI Payment</p>
                <p className="font-body text-xs text-muted-foreground mt-1 mb-4">Checkout par ye UPI ID aur naam QR ke saath dikhega.</p>
                <label className="overline text-muted-foreground">UPI ID</label>
                <input className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.upi_id || ""} onChange={(e) => setSettings({ ...settings, upi_id: e.target.value })} data-testid="settings-upi-id" placeholder="8946808370@ybl" />
                <label className="overline text-muted-foreground mt-4 block">UPI account holder name</label>
                <input className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.upi_name || ""} onChange={(e) => setSettings({ ...settings, upi_name: e.target.value })} data-testid="settings-upi-name" placeholder="Pushpendra Singh Chundawat" />
              </div>

              <div className="border-t border-border pt-6 mt-2">
                <p className="font-display text-2xl">Invoice / GST</p>
                <p className="font-body text-xs text-muted-foreground mt-1 mb-4">Ye invoice (bill) par dikhega. GST % 0 rakho to bill par tax line nahi aayegi.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-muted-foreground">GST %</label>
                    <input type="number" min="0" max="28" className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.gst_percent || 0} onChange={(e) => setSettings({ ...settings, gst_percent: parseFloat(e.target.value) || 0 })} data-testid="settings-gst" placeholder="5" />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground">GSTIN</label>
                    <input className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.gstin || ""} onChange={(e) => setSettings({ ...settings, gstin: e.target.value })} data-testid="settings-gstin" placeholder="08XXXXX1234X1Z5" />
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-6 mt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display text-2xl">Sale / Offer</p>
                    <p className="font-body text-xs text-muted-foreground mt-1">Jab chahe offer on/off karein.</p>
                  </div>
                  <button type="button" onClick={() => setSettings({ ...settings, promo_enabled: !settings.promo_enabled })} data-testid="promo-toggle"
                    className={`w-14 h-8 rounded-full relative transition-colors ${settings.promo_enabled ? "bg-[hsl(var(--primary))]" : "bg-muted-foreground/30"}`}>
                    <span className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${settings.promo_enabled ? "left-7" : "left-1"}`} />
                  </button>
                </div>
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="overline text-muted-foreground">Offer banner text (top of website)</label>
                    <input className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.promo_text || ""} onChange={(e) => setSettings({ ...settings, promo_text: e.target.value })} data-testid="promo-text" placeholder="✦ Diwali Sale — Flat 20% OFF on all shirts ✦" />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground">Discount % (checkout par apply hoga, 0 = sirf banner)</label>
                    <input type="number" min="0" max="90" className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.promo_discount || 0} onChange={(e) => setSettings({ ...settings, promo_discount: parseFloat(e.target.value) || 0 })} data-testid="promo-discount" placeholder="20" />
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-6 mt-2">
                <p className="font-display text-2xl">Home page background</p>
                <p className="font-body text-xs text-muted-foreground mt-1 mb-4">Website ke starting (hero) section ki background photo.</p>
                <div className="aspect-[16/7] bg-muted border border-border overflow-hidden mb-3">
                  <img src={settings.hero_image ? imageUrl(settings.hero_image) : "https://images.unsplash.com/photo-1783764245498-31991c8a6cae?crop=entropy&cs=srgb&fm=jpg&q=60&w=800"} alt="Hero preview" className="w-full h-full object-cover" data-testid="hero-preview" />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button type="button" onClick={() => heroFileRef.current?.click()} className="inline-flex items-center gap-2 border border-border px-4 py-2.5 font-body text-sm hover:border-foreground transition-colors" data-testid="hero-upload-btn">
                    <Upload className="w-4 h-4" strokeWidth={1.5} /> Upload photo
                  </button>
                  <input className="flex-1 border border-border bg-background px-3 py-2.5 font-body text-sm focus:outline-none focus:border-foreground" placeholder="Ya image URL paste karein" value={settings.hero_image || ""} onChange={(e) => setSettings({ ...settings, hero_image: e.target.value })} data-testid="hero-url-input" />
                </div>
                <input ref={heroFileRef} type="file" accept="image/*" className="hidden" onChange={uploadHero} />
              </div>

              <div className="border-t border-border pt-6 mt-2">
                <p className="font-display text-2xl">Opening balance (shuruaati)</p>
                <p className="font-body text-xs text-muted-foreground mt-1 mb-4">Abhi Cash / UPI / Bank mein kitna paisa hai — Dashboard ka balance isi se shuru hoga. Ye sirf admin ko dikhta hai.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="overline text-muted-foreground">Cash ₹</label>
                    <input type="number" min="0" className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.opening_cash ?? 0} onChange={(e) => setSettings({ ...settings, opening_cash: parseFloat(e.target.value) || 0 })} data-testid="settings-opening-cash" />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground">UPI ₹</label>
                    <input type="number" min="0" className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.opening_upi ?? 0} onChange={(e) => setSettings({ ...settings, opening_upi: parseFloat(e.target.value) || 0 })} data-testid="settings-opening-upi" />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground">Bank ₹</label>
                    <input type="number" min="0" className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={settings.opening_bank ?? 0} onChange={(e) => setSettings({ ...settings, opening_bank: parseFloat(e.target.value) || 0 })} data-testid="settings-opening-bank" />
                  </div>
                </div>
              </div>

              <button onClick={saveSettings} data-testid="settings-save" className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-6 py-3 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors">
                <Save className="w-4 h-4" strokeWidth={1.5} /> Save details
              </button>
            </div>

            <div className="space-y-4 border border-border p-8 bg-card mt-8 max-w-xl" data-testid="change-password-card">
              <div>
                <p className="font-display text-2xl">Change admin password</p>
                <p className="font-body text-xs text-muted-foreground mt-1">Security ke liye apna password kabhi bhi badlein (kam se kam 8 characters).</p>
              </div>
              <div>
                <label className="overline text-muted-foreground">Current password</label>
                <input type="password" className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} data-testid="current-password" placeholder="Abhi ka password" />
              </div>
              <div>
                <label className="overline text-muted-foreground">New password</label>
                <input type="password" className="w-full border border-border bg-background px-3 py-2.5 font-body text-sm mt-2 focus:outline-none focus:border-foreground" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} data-testid="new-password" placeholder="Naya password (min 8 characters)" />
              </div>
              <button onClick={changePassword} data-testid="change-password-btn" className="inline-flex items-center gap-2 bg-[hsl(var(--secondary))] text-white px-6 py-3 font-body text-sm hover:bg-[hsl(var(--primary))] transition-colors">
                Update password
              </button>
            </div>
          </div>
        )}
      </main>

      <ProductModal open={modal} onClose={() => setModal(false)} onSaved={loadProducts} editing={editing} categories={categories} />
    </div>
  );
}
